import { AddressesQuery, AddressesService } from './../../state/addresses';
import { Injectable } from '@angular/core';
import { PeopleQuery, PeopleService } from '../../state/people';
import { VehiclesQuery, VehiclesService } from '../../state/vehicles';
import { FormatService } from '../format/format.service';
import { RentalHomesQuery, RentalHomesService } from '../../state/rental-homes';
import { WorksQuery, WorksService } from '../../state/works';
import { HomesQuery, HomesService } from '../../state/homes';
import { RetirementsQuery, RetirementsService } from '../../state/retirements';
import { QueryEntity } from '@datorama/akita';
import { AccountService } from '@core/state/account';
import { RelationsService } from '@core/state/relations';
import { EducationsQuery, EducationsService } from '@core/state/educations';
import { MaritalStatusesQuery, MaritalStatusesService } from '@core/state/marital-statuses';
import { ConstructionTypesQuery, ConstructionTypesService } from '@core/state/relation-configs/construction_types';
import { HomeTypesQuery, HomeTypesService } from '@core/state/home-types';
import { JsonApiEntityService } from '@core/state/jsonentity/json-api-entity.service';
import Apiable from '@core/state/jsonentity/interfaces/apiable';
import { Observable, of, zip } from 'rxjs';
import { FinancialCompaniesQuery, FinancialCompaniesService } from '@core/state/financial-companies';
import { MortgageQuery } from '@core/state/mortgage/mortgage.query';
import { MortgageService } from '@core/state/mortgage/mortgage.service';
import { MortgageTypesQuery, MortgageTypesService } from '@core/state/mortgage-types';
import { FieldConfig } from '@core/components/crudbuilder/models/field-config';
import { LifeInsuranceQuery, LifeInsuranceService } from '@core/state/life-insurance';
import { FieldGroup } from '@core/pages/crud/dependent-fields/field-group';

@Injectable({
  providedIn: 'root'
})
export class PopularAppStatesService {
  public people$ = this.peopleQuery.selectAll();

  constructor(
    public peopleService: PeopleService,
    private peopleQuery: PeopleQuery,
    public vehiclesService: VehiclesService,
    private vehiclesQuery: VehiclesQuery,
    public rentalHomesService: RentalHomesService,
    private rentalHomesQuery: RentalHomesQuery,
    public workService: WorksService,
    private worksQuery: WorksQuery,
    public homesService: HomesService,
    private homesQuery: HomesQuery,
    public retirementsService: RetirementsService,
    private retirementsQuery: RetirementsQuery,
    public addressesService: AddressesService,
    private addressesQuery: AddressesQuery,
    public relationsService: RelationsService,
    private educationsQuery: EducationsQuery,
    public educationsService: EducationsService,
    private constructionTypesQuery: ConstructionTypesQuery,
    public constructionTypesService: ConstructionTypesService,
    public format: FormatService,
    public account: AccountService,
    private maritalStatusesQuery: MaritalStatusesQuery,
    private maritalStatusesService: MaritalStatusesService,
    private homeTypesService: HomeTypesService,
    private homeTypesQuery: HomeTypesQuery,
    private financialCompaniesQuery: FinancialCompaniesQuery,
    private financialCompaniesService: FinancialCompaniesService,
    private mortgagesQuery: MortgageQuery,
    private mortgagesService: MortgageService,
    private mortgageTypesQuery: MortgageTypesQuery,
    private mortgageTypesService: MortgageTypesService,
    private lifeInsuranceService: LifeInsuranceService,
    private lifeInsuranceQuery: LifeInsuranceQuery,
  ) {
  }

  // TODO: Populate this with the values from below
  static readonly TYPE_MORTGAGE          = 'mortgage';
  static readonly TYPE_MORTGAGE_TYPE     = 'mortgage_type';
  static readonly TYPE_FINANCIAL_COMPANY = 'financial_company';
  static readonly TYPE_LIFE_INSURANCE    = 'life_insurance';

  /**
   * Find fields that need a query on the server, then call ready.
   * Used on read pages.
   */
  public prefetchRead(readFields: Array<FieldConfig | string | FieldGroup>, model) {
    let subs: Array<Observable<any>> = [];

    for (const field of readFields) {
      if (field instanceof FieldConfig && model[field.name]) {
        let sub = null;
        switch (field.type) {
          case FieldConfig.TYPE_FINANCIAL_COMPANIES:
            sub = this.getFromStoreOrFromApi(PopularAppStatesService.TYPE_FINANCIAL_COMPANY, model[field.name]);
            break;

          // RelationConfigs
          // They usually have the name in the request, so no need to prefetch on read.
          case FieldConfig.TYPE_LIFE_INSURANCE:
          case FieldConfig.TYPE_RELATIONCONFIG:
            sub = of({});
            break;
        }

        if (sub instanceof Observable) {
          subs.push(sub);
        }
      }
    }

    // Fix for issue when zip is empty or only contains 1 item.
    subs.push(of({}));
    subs.push(of({}));

    return zip(...subs);
  }

  /**
   * Gets from cache if the model is found.
   * Gets from API otherwise.
   */
  public getFromStoreOrFromApi(type, id) {
    const taken = this.getOneFromLocalStore(type, id);

    // if ! taken.
    if (!taken) {
      return this.getService(type).get(id);
    } else {
      return of(taken);
    }
  }

  /**
   * Get all from the local store.
   * @param type
   */
  public getAllStored(type) {
    return this.getQuery(type).getAll();
  }

  public getOneFromLocalStore(type, id): any {
    return this.getQuery(type).getEntity(id);
  }

  /**
   */
  public getQuery(type: string): QueryEntity<any> {
    switch (type) {
      case 'person':
        return this.peopleQuery;
      case 'vehicle':
        return this.vehiclesQuery;
      case 'rental-home':
        return this.rentalHomesQuery;
      case 'work':
        return this.worksQuery;
      case 'home':
        return this.homesQuery;
      case 'retirement':
        return this.retirementsQuery;
      case 'address':
        return this.addressesQuery;
      case 'education':
        return this.educationsQuery;
      case 'construction-type':
        return this.constructionTypesQuery;
      case 'marital_status':
        return this.maritalStatusesQuery;
      case 'home-type':
        return this.homeTypesQuery;
      case 'financial_company':
        return this.financialCompaniesQuery;
      case PopularAppStatesService.TYPE_MORTGAGE:
        return this.mortgagesQuery;
      case PopularAppStatesService.TYPE_MORTGAGE_TYPE:
        return this.mortgageTypesQuery;
      case PopularAppStatesService.TYPE_LIFE_INSURANCE:
        return this.lifeInsuranceQuery;
    }
  }

  /**
   * @return JsonApiEntityService
   */
  public getService(state: string): Apiable {
    switch (state) {
      case 'person':
        return this.peopleService;
      case 'vehicle':
        return this.vehiclesService;
      case 'rental-home':
        return this.rentalHomesService;
      case 'work':
        return this.workService;
      case 'home':
        return this.homesService;
      case 'retirement':
        return this.retirementsService;
      case 'address':
        return this.addressesService;
      case 'education':
        return this.educationsService;
      case 'construction-type':
        return this.constructionTypesService;
      case 'home-type':
        return this.homeTypesService;
      case 'financial_company':
        return this.financialCompaniesService;
      case PopularAppStatesService.TYPE_MORTGAGE:
        return this.mortgagesService;
      case PopularAppStatesService.TYPE_MORTGAGE_TYPE:
        return this.mortgageTypesService;
      case PopularAppStatesService.TYPE_LIFE_INSURANCE:
        return this.lifeInsuranceService;
    }
  }
}
